import { ThemeEvents, CartUpdateEvent } from "@theme/events";

const ERR_GENERIC = "ERR_GENERIC";
const ERR_SESSION_CONFLICT = "ERR_SESSION_CONFLICT";

const ERROR_MESSAGES_MAP = {
  "sold out": "ERR_OUT_OF_STOCK",
  "limit reached": "ERR_QTY_LIMIT",
  "to the cart": "ERR_IN_CART_LIMIT",
};

/**
 * Reset cart and add new items
 * @param {Array<Object<string, any>>} items
 */
export async function resetCartAndAddItems(items) {
  await clearCart(false); // don't trigger cart update event

  const cartLoader = document.querySelector(".js-warranty-cart-loader");
  cartLoader?.classList.remove("hidden");

  const isOk = await addItemsToCart(items);
  cartLoader?.classList.add("hidden");

  return isOk;
}

/**
 * Get section ids for cart refresh
 */
function getSectionId() {
  const cartItemsComponents = document.querySelectorAll("cart-items-component");
  const sectionsToUpdate = new Set();

  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionsToUpdate).filter(Boolean);
}

/**
 * Clear cart
 */
async function clearCart(triggerEvent = true) {
  try {
    const sectionId = getSectionId();

    const response = await fetch("/cart/clear.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sections: sectionId }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Cart clear error:", data);
      const error = getErrorDetails(data.description, response.status);
      throw error;
    }

    await clearWarrantySourceAttribute();

    if (triggerEvent) {
      document.dispatchEvent(new CartUpdateEvent({}, "warranty-loader", { sections: data.sections }));
    }

  } catch (error) {
    console.error("Error clearing cart:", error);
    handleError(error);
  }
}

/**
 * Add items to cart
 * @param {Array<Object<string, any>>} items
 * @returns {Promise<boolean>}
 */
async function addItemsToCart(items) {
  try {
    const sectionId = getSectionId();

    await setWarrantySourceAttribute();

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items,
        sections: sectionId,
        order_type: "warranty",
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Shopify cart error:", data);
      const error = getErrorDetails(data.description, response.status);
      throw error;
    }

    document.dispatchEvent(new CartUpdateEvent({}, "warranty-loader", { sections: data.sections }));

    return true;
  } catch (error) {
    console.error("Error adding items:", error);
    handleError(error);
    return false;
  }
}

/**
 * Set _Warranty_Source cart attribute
 * @returns {Promise<void>}
 */
async function setWarrantySourceAttribute() {
  try {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          _Warranty_Source: "Authorized_App",
        },
      }),
    });
  } catch (error) {
    console.error("Error setting warranty source attribute:", error);
  }
}

/**
 * Clear _Warranty_Source cart attribute
 * @returns {Promise<void>}
 */
async function clearWarrantySourceAttribute() {
  try {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          _Warranty_Source: "",
        },
      }),
    });
  } catch (error) {
    console.error("Error clearing warranty source attribute:", error);
  }
}

/**
 * Get error details
 * @param {string} text
 * @param {number} status
 * @returns {Error}
 */
function getErrorDetails(text, status) {
  const error = new Error(text || "Cart add failed");
  //@ts-expect-error
  error.status = status;
  return error;
}

/**
 * Handle error
 * @param {any} error
 */
function handleError(error) {
  const { code, message } = parseErrorMessage(error);

  if (!code || !message) return;

  if (code === ERR_SESSION_CONFLICT || code === ERR_GENERIC) {
    showStartOverLink();
  }

  showErrorMessage(message);
}

/**
 * Parse error message
 * @param {any} error
 * @returns {{ code: string, message: string | null }}
 */
function parseErrorMessage(error) {
  const status = error?.status;
  const message = (error?.message || "").toLowerCase();
  let errorCaseKey = null;

  // Inventory / availability issues
  if (status === 422) {
    errorCaseKey = Object.keys(ERROR_MESSAGES_MAP).find((key) =>
      message.includes(key)
    );
    // Access issues (password, proxy, firewalls, etc)
  } else if ([401, 403, 404].includes(status)) {
    errorCaseKey = ERR_SESSION_CONFLICT;
    // Bad request, Rate limiting, or Server error
  } else if (status === 400 || status === 429 || status >= 500) {
    errorCaseKey = ERR_GENERIC;
  }

  //@ts-expect-error
  const errorCode = ERROR_MESSAGES_MAP[errorCaseKey] || errorCaseKey || ERR_GENERIC;
  const translations = document.querySelector(".js-warranty-cart-error-message");
  const dataAttr = `data-${errorCode.toLowerCase().replace(/_/g, "-")}-message`;
  const errorMessage = translations?.getAttribute(dataAttr) || "";

  return {
    code: errorCode,
    message: errorMessage,
  };
}

/**
 * Show error message
 * @param {string} message
 * @returns {void}
 */
function showErrorMessage(message) {
  if (!message) return;

  const cartDrawer = document.querySelector("cart-drawer-component");
  const errorMessageElement = cartDrawer?.querySelector(".js-warranty-cart-error-message");
  const messageEl = errorMessageElement?.querySelector(".js-message");

  if (!messageEl) return;

  messageEl.textContent = message || "";
  errorMessageElement?.classList.remove("hidden");

  setTimeout(() => {
    errorMessageElement?.classList.add("hidden");
  }, 7000);
}

/**
 * Show start over link
 * @returns {void}
 */
function showStartOverLink() {
  const startOverLink = document.querySelector(".js-warranty-start-over-link");
  startOverLink?.classList.remove("hidden");
}

/**
 * Clear session
 * @returns {void}
 */
function clearSession() {
  const keysToClear = ['cart_cache', 'shopify-cart', 'shopify_locale', 'currency', 'locale'];

  keysToClear.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  Object.keys(localStorage)
    .filter(key => key.includes('cart'))
    .forEach(key => localStorage.removeItem(key));

  // Expire Shopify Cart & Localization Cookies
  const cookies = ['cart', 'cart_sig', 'cart_ts', 'cart_currency', 'localization'];
  cookies.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
}

/**
 * Handle start over link click
 * @param {MouseEvent} event
 * @returns {Promise<void>}
 */
async function handleStartOverLinkClick(event) {
  event.preventDefault();

  await clearCart();
  clearSession();
  //@ts-expect-error — Shopify.routes.root
  window.location.href = window.Shopify.routes.root + "pages/warranty";
}

/**
 * Cart update handler
 */
async function onCartUpdate() {
  await getWarrantyClaimItems();
}

/**
 * Check warranty claim items in cart
 */
async function getWarrantyClaimItems() {
  try {
    const res = await fetch("/cart.js");
    const cart = await res.json();

    if (cart.items.length == 0) {
      return;
    }

    const warrantyItem = cart.items.find(
      (/** @type {any} */ item) => item.properties && item.properties["_Warranty_Claim_Type"],
    );

    if (!warrantyItem) {
      console.log("No warranty claim product found.");
    }

    //@ts-expect-error
    const otterVars = window?.OtterVars || {};
    const feeIdAuthorized = Number(otterVars.feeIdAuthorized);
    const feeIdUnauthorized = Number(otterVars.feeIdUnauthorized);

    if (!feeIdAuthorized && !feeIdUnauthorized) {
      console.error("Invalid fee IDs:", otterVars.feeIdAuthorized, otterVars.feeIdUnauthorized);
      return;
    }

    const feeItem = cart.items.find(
      (/** @type {any} */ item) => item.variant_id === feeIdAuthorized || item.variant_id === feeIdUnauthorized,
    );

    if (feeItem && warrantyItem) {
      console.log("Both items found:", { feeItem, warrantyItem });
    } else if (feeItem || warrantyItem) {
      const errorMessage = `${!feeItem ? "Fee" : "Warranty"} item missing. Clearing cart.`;

      await clearCart();
      console.log(errorMessage);
      handleError(getErrorDetails(errorMessage, 404));
    }
  } catch (error) {
    console.error("Cart fetch error:", error);
    handleError(getErrorDetails(error.message, 404));
  }
}

function init() {
  // Listen for cart updates
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);

  // Start over link listener
  const startOverLink = document.querySelector(".js-warranty-start-over-link");
  startOverLink?.addEventListener("click", handleStartOverLinkClick);
}

init();

/**
 * Example items to add
 *
const isAuthorized = true; // determined by warranty data
const feeIdAuthorized = Number(window?.OtterVars?.feeIdAuthorized);
const feeIdUnauthorized = Number(window?.OtterVars?.feeIdUnauthorized);
const feeId = isAuthorized ? feeIdAuthorized : feeIdUnauthorized;

if (!feeId) {
  console.error("Invalid fee product ID");
}

const items = [
  {
    id: 48067129114880, // warranty product variant
    quantity: 1,
    properties: {
      "_Damage_Details": "1 year",
      "_Original_Order_ID": "WarrantyLoader",
      "_Warranty_Claim_Type": "test"
    }
  },
  {
    id: feeId, // fee variant (authorized or unauthorized)
    quantity: 1,
    properties: {
      "_Parent_Item": "48067129114880"
    }
  }
];
resetCartAndAddItems(items);
*/
